import { Component, computed, OnInit } from '@angular/core';
import { MatCard, MatCardContent } from '@angular/material/card';
import { combineLatest, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DynamicObservable } from '../../../../core/classes/dynamic-observable';
import { DatatableComponent } from '../../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../../shared/components/datatable/datatable.interface';
import { IBaseDatatableState } from '../../../../state/base-datatable-store.service';
import { FormUtilitiesService } from '../../../../utilities/form-utilities.service';
import { BaseCrudPageComponent } from '../../../app/base-crud-page.component';
import { DataSourcesAddEditDialogComponent } from './data-sources-add-edit-dialog/data-sources-add-edit-dialog.component';
import { IDataSourceAddEditDialogData } from './data-sources-add-edit-dialog/data-sources-add-edit-dialog.interface';
import { IDataSource } from './data-sources.interface';
import { DataSourceStore } from './data-sources.store';

@Component({
  imports: [DatatableComponent, MatCard, MatCardContent],
  providers: [DataSourceStore],
  selector: 'app-data-sources',
  styleUrl: './data-sources.component.scss',
  templateUrl: './data-sources.component.html',
})
export class DataSourcesComponent
  extends BaseCrudPageComponent<IDataSource, IBaseDatatableState<IDataSource>>
  implements OnInit
{
  public readonly formattedDatatableRows = computed(() => {
    return this.store.data$();
  });

  public readonly datatableColumns: IDatatableColumn<IDataSource>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
  ];

  public datatableConfigurations: IDatatableConfiguration<IDataSource> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.bulk,
    editScope: ETableCrudScope.single,
  };

  private readonly afterCreateUpdateSuccess = new DynamicObservable<unknown>(null);

  constructor(
    public readonly store: DataSourceStore,
    public readonly formService: FormUtilitiesService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfDataSource',
      multiContext: 'field.dataSources',
      nameProperty: 'name',
      singleContext: 'field.dataSource',
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        afterCreateUpdateSuccess: this.afterCreateUpdateSuccess.obs$,
        datatable: this.datatableParams$.pipe(filter(Boolean)),
      }).subscribe(({ datatable }) => {
        this.store.loadDataSources({
          limit: datatable.limit,
          page: datatable.page,
          sort: [datatable.sort],
          ...(datatable.search ? { search: { searchedFields: ['name'], searchText: datatable.search } } : {}),
        });
      }),
    );
  }

  public initializeAddEdit(isEdit: boolean): void {
    if (isEdit) {
      this.setupForEdit();
    } else {
      this.setupForAdd();
    }

    const data: IDataSourceAddEditDialogData = {
      dialogTitle: this.addEditModalTitle(),
      submitButtonText: this.addEditSubmitButtonText(),
      ...(this.isEditDialog()
        ? {
            editData: this.selectedRows()[0],
          }
        : {}),
    };

    this.afterCreateUpdateSuccess.switch(
      this.matDialog
        .open(DataSourcesAddEditDialogComponent, {
          ...DataSourcesAddEditDialogComponent.dialogConfig,
          data,
        })
        .afterClosed()
        .pipe(take(1)),
    );
  }
}
